import { Check, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';

export interface StepperStep {
  id: string;
  label: string;
  optional?: boolean;
}

export interface StepperProps {
  steps: Array<StepperStep>;
  currentStep: number;
  children: ReactNode;
  canGoNext?: boolean;
  isSubmitting?: boolean;
  allowJumpToCompleted?: boolean;
  onStepChange?: (step: number) => void;
  onBack?: () => void;
  onNext?: () => void;
  onFinish?: () => void;
  backLabel?: string;
  nextLabel?: string;
  finishLabel?: string;
  header?: ReactNode;
  footerStart?: ReactNode;
  className?: string;
}

export function Stepper({
  steps,
  currentStep,
  children,
  canGoNext = true,
  isSubmitting = false,
  allowJumpToCompleted = true,
  onStepChange,
  onBack,
  onNext,
  onFinish,
  backLabel = 'Back',
  nextLabel = 'Continue',
  finishLabel = 'Finish',
  header,
  footerStart,
  className,
}: StepperProps) {
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className={cn('flex min-h-0 flex-col gap-6', className)}>
      {header}
      <nav aria-label='Progress'>
        <ol className='grid gap-3 sm:grid-cols-3'>
          {steps.map((step, index) => {
            const isComplete = index < currentStep;
            const isCurrent = index === currentStep;
            const canSelect =
              allowJumpToCompleted && isComplete && Boolean(onStepChange);

            return (
              <li key={step.id}>
                <button
                  type='button'
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                    isCurrent && 'border-primary bg-primary/5',
                    isComplete && 'border-primary/40',
                    canSelect && 'hover:bg-muted',
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                  disabled={!canSelect}
                  onClick={() => onStepChange?.(index)}
                >
                  <span
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                      isCurrent &&
                        'border-primary bg-primary text-primary-foreground',
                      isComplete &&
                        'border-primary bg-primary text-primary-foreground',
                    )}
                  >
                    {isComplete ? <Check className='size-4' /> : index + 1}
                  </span>
                  <span className='min-w-0'>
                    <span className='block truncate text-sm font-medium'>
                      {step.label}
                    </span>
                    {step.optional && (
                      <span className='block text-xs text-muted-foreground'>
                        Optional
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className='min-h-0 flex-1'>{children}</div>

      <div className='sticky bottom-0 flex items-center justify-between gap-3 border-t bg-background py-4'>
        <div>{footerStart}</div>
        <div className='flex items-center gap-2'>
          {currentStep > 0 && onBack && (
            <Button
              type='button'
              variant='outline'
              disabled={isSubmitting}
              onClick={onBack}
            >
              {backLabel}
            </Button>
          )}
          {isLastStep ? (
            <Button
              type='button'
              disabled={!canGoNext || isSubmitting}
              onClick={onFinish}
            >
              {isSubmitting && <Loader2 className='animate-spin' />}
              {finishLabel}
            </Button>
          ) : (
            <Button
              type='button'
              disabled={!canGoNext || isSubmitting}
              onClick={onNext}
            >
              {nextLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
